import { Copy, Ellipsis, Package, Pencil, Trash } from 'lucide-react'
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import useSWR from 'swr'

import { ParameterChecklist } from '@/components/Catalog/ParameterChecklist'
import { PriceTimelineChart } from '@/components/Catalog/PriceTimelineChart'
import { SkuGallery } from '@/components/Catalog/SkuGallery'
import { VariantAxisChecklist } from '@/components/Catalog/VariantAxisChecklist'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { Money, PageHeader, SegmentedTabs, StatusBadge } from '@/components/ds'
import { EmptyState } from '@/components/EmptyState'
import { Loading } from '@/components/Loading'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ProductVariants } from '@/containers/Products/ProductVariants'
import { StockItems } from '@/containers/StockItems/StockItems'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import { ProductPriceForm } from '@/forms/ProductPriceForm/ProductPriceForm'
import { SkuCreateForm } from '@/forms/SkuCreateForm/SkuCreateForm'
import { SkuEditForm } from '@/forms/SkuEditForm/SkuEditForm'
import { StockBinLocationForm } from '@/forms/StockBinLocationForm/StockBinLocationForm'
import { StockTransferForm } from '@/forms/StockTransferForm/StockTransferForm'
import { useFeatureCheck } from '@/hooks/useFeatureCheck'
import { Category } from '@/interfaces/catalog/Category'
import { Sku } from '@/interfaces/catalog/Sku'
import { StockItem } from '@/interfaces/warehouse/StockItem'
import { Warehouse } from '@/interfaces/warehouse/Warehouse'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'
import {
  addToStock,
  CatalogProduct,
  CatalogSku,
  emptyStock,
  formatCount,
  productTypeLabel,
  StockSummary,
  variantLabel,
} from '@/lib/catalog/catalogTypes'
import { getProductAxisIds, getProductParameterInputs, ProductParameterInput, saveProductAxes, saveProductParameters } from '@/lib/catalog/productStructure'
import { ALL_SKUS_TAB, getSystemSku, getUserSkus, resolvePriceSkuId } from '@/lib/catalog/skuUtils'
import { formatDate } from '@/lib/formats/formatDate'
import { components } from '@invo.space/shared'

type SkuPrice = components['schemas']['SkuPrice']

type SkuPricesResponse = {
  data?: {
    current?: SkuPrice
    history?: SkuPrice[]
  }
}

const Panel = ({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) => (
  <section className="rounded-lg border p-5">
    <div className="mb-4 flex items-center justify-between gap-3">
      <h2 className="text-sm font-medium">{title}</h2>
      {action}
    </div>
    {children}
  </section>
)

const categoryPath = (categories: Category[], id?: string | null) => {
  if (!id) return null
  const byId = new Map(categories.map((c) => [c.id, c]))
  const names: string[] = []
  let current = byId.get(id)
  while (current && names.length < 10) {
    names.unshift(current.name)
    current = current.parentId ? byId.get(current.parentId) : undefined
  }
  return names.length ? names.join(' > ') : null
}

export default function ProductDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation(undefined, { keyPrefix: 'catalog' })
  const historyRef = useRef<HTMLElement>(null)

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isCreateSkuDialogOpen, setIsCreateSkuDialogOpen] = useState(false)
  const [isCreateSkuSubmitting, setIsCreateSkuSubmitting] = useState(false)
  const [skuToEdit, setSkuToEdit] = useState<Sku | null>(null)
  const [isEditSkuSubmitting, setIsEditSkuSubmitting] = useState(false)
  const [skuToDelete, setSkuToDelete] = useState<Sku | null>(null)
  const [stockItemToTransfer, setStockItemToTransfer] = useState<StockItem | undefined>()
  const [stockItemToEditLocation, setStockItemToEditLocation] = useState<StockItem | undefined>()
  const [selectedTab, setSelectedTab] = useState<string | undefined>()
  const [savingActive, setSavingActive] = useState(false)
  const [isAssignAxesOpen, setIsAssignAxesOpen] = useState(false)
  const [isAssignParametersOpen, setIsAssignParametersOpen] = useState(false)
  const [selectedAxisIds, setSelectedAxisIds] = useState<string[]>([])
  const [selectedParameters, setSelectedParameters] = useState<ProductParameterInput[]>([])
  const [isAssigningAxes, setIsAssigningAxes] = useState(false)
  const [isAssigningParameters, setIsAssigningParameters] = useState(false)
  const [isGeneratingSkus, setIsGeneratingSkus] = useState(false)

  const { data: product, isLoading, mutate: mutateProduct } = useSWR<CatalogProduct>(
    id && `/api/v2/products/${id}`,
    apiFetcher,
  )

  const {
    data: skusResponse,
    isLoading: isSkusLoading,
    mutate: mutateSkus,
  } = useSWR<{ data?: CatalogSku[]; total?: number }>(
    id ? `/api/v2/skus?productId=${id}&includeSystem=true` : null,
    apiFetcher,
  )

  const skus = useMemo(() => skusResponse?.data ?? [], [skusResponse])
  const userSkus = getUserSkus(skus) as CatalogSku[]
  const systemSku = getSystemSku(skus) as CatalogSku | undefined
  const variantSkus = userSkus.length ? userSkus : systemSku ? [systemSku] : []
  const showAllSkusTab = userSkus.length > 1
  const activeTab = showAllSkusTab ? (selectedTab ?? ALL_SKUS_TAB) : selectedTab
  const activeSkuId = resolvePriceSkuId(skus, activeTab ?? '')
  const isAllSkusTab = showAllSkusTab && activeTab === ALL_SKUS_TAB
  const gallerySku = skus.find((sku) => sku.id === activeSkuId)

  const {
    data: priceHistoryResponse,
    isLoading: isPriceHistoryLoading,
    mutate: mutatePriceHistory,
  } = useSWR<SkuPricesResponse>(activeSkuId ? `/api/v2/skus/${activeSkuId}/prices` : null, apiFetcher)
  const priceHistory = priceHistoryResponse?.data?.history ?? []

  const { isEnabled: warehouseEnabled } = useFeatureCheck('warehouse')

  const {
    data: stockItemsResponse,
    isLoading: isStockLoading,
    mutate: mutateStockItems,
  } = useSWR<{ data?: StockItem[]; total?: number }>(warehouseEnabled ? '/api/v2/stock-items' : null, apiFetcher)

  const { data: warehousesResponse } = useSWR<{ data?: Warehouse[]; total?: number }>(
    warehouseEnabled ? '/api/v2/warehouses' : null,
    apiFetcher,
  )
  const warehouses = warehousesResponse?.data ?? []

  const { data: categoriesResponse } = useSWR<{ data?: Category[]; total?: number }>(
    product ? '/api/v2/categories?includeChildren=true' : null,
    apiFetcher,
  )
  const categories = categoriesResponse?.data || []
  const mainCategoryPath = categoryPath(categories, product?.mainCategoryId)
  const secondaryCategories = categories.filter((cat) => product?.secondaryCategoryIds?.includes(cat.id))

  const skuById = useMemo(() => new Map(skus.map((sku) => [sku.id, sku])), [skus])

  const productStockItems = useMemo(
    () =>
      (stockItemsResponse?.data ?? [])
        .filter((item) => skuById.has(item.skuId))
        .sort((a, b) => (a.skuName ?? '').localeCompare(b.skuName ?? '', 'cs')),
    [stockItemsResponse, skuById],
  )

  const stockBySkuId = useMemo(() => {
    if (!warehouseEnabled) return null
    const map = new Map<string, StockSummary>()
    for (const item of productStockItems) {
      const summary = map.get(item.skuId) ?? emptyStock()
      addToStock(summary, item)
      map.set(item.skuId, summary)
    }
    return map
  }, [productStockItems, warehouseEnabled])

  const assignedAxes = product?.variantAxes ?? []

  useEffect(() => {
    if (!isAssignAxesOpen || !product) {
      return
    }

    setSelectedAxisIds(getProductAxisIds(product))
  }, [isAssignAxesOpen, product])

  useEffect(() => {
    if (!isAssignParametersOpen || !product) {
      return
    }

    setSelectedParameters(getProductParameterInputs(product))
  }, [isAssignParametersOpen, product])

  useSetPageHeader({
    breadcrumbs: [
      { href: '/catalog', children: t('title') },
      { href: '/catalog/products', children: t('products.title') },
      ...(!isLoading && !product ? [] : [{ href: `/catalog/product/${id}`, children: product?.name || id || '' }]),
    ],
  })

  const refreshCatalog = () => {
    mutateSkus()
    mutatePriceHistory()
  }

  const handleDeleteProduct = async (): Promise<void> => {
    if (!product?.id) return
    try {
      await API.delete(`/api/v2/products/${product.id}`)
      toast.success(t('products.delete.success'))
      navigate('/catalog/products')
    } catch (error: unknown) {
      console.error('Delete error:', error)
      toast.error(t('products.delete.error'), {
        description: extractErrorMessage(error) || t('products.error.description'),
      })
      throw error
    }
  }

  const handleDeleteSku = async (): Promise<void> => {
    if (!skuToDelete) return
    try {
      await API.delete(`/api/v2/skus/${skuToDelete.id}`)
      toast.success(t('products.detail.skus.delete.success'))
      if (selectedTab === skuToDelete.id) setSelectedTab(undefined)
      await mutateSkus()
      await mutatePriceHistory()
      setSkuToDelete(null)
    } catch (error: unknown) {
      console.error('SKU delete error:', error)
      toast.error(t('products.detail.skus.delete.error'), {
        description: extractErrorMessage(error) || t('products.error.description'),
      })
      throw error
    }
  }

  const toggleActive = async (isActive: boolean) => {
    if (!product) return
    setSavingActive(true)
    try {
      await API.patch(`/api/v2/products/${product.id}`, { isActive })
      await mutateProduct()
      toast.success(isActive ? 'Produkt je aktivní' : 'Produkt je neaktivní')
    } catch (error: unknown) {
      toast.error(t('products.edit.error'), { description: extractErrorMessage(error) || undefined })
    } finally {
      setSavingActive(false)
    }
  }

  const handleAssignAxes = async () => {
    if (!id) {
      return
    }

    setIsAssigningAxes(true)
    try {
      await saveProductAxes(id, selectedAxisIds)
      toast.success(t('products.detail.variants.assign.success'))
      await mutateProduct()
      setIsAssignAxesOpen(false)
    } catch (error: unknown) {
      console.error('Assign axes error:', error)
      toast.error(t('products.detail.variants.assign.error'), {
        description: extractErrorMessage(error) || t('products.error.description'),
      })
    } finally {
      setIsAssigningAxes(false)
    }
  }

  const handleAssignParameters = async () => {
    if (!id) {
      return
    }

    setIsAssigningParameters(true)
    try {
      await saveProductParameters(id, selectedParameters)
      toast.success(t('products.detail.parameters.assign.success'))
      await mutateProduct()
      setIsAssignParametersOpen(false)
    } catch (error: unknown) {
      console.error('Assign parameters error:', error)
      toast.error(t('products.detail.parameters.assign.error'), {
        description: extractErrorMessage(error) || t('products.error.description'),
      })
    } finally {
      setIsAssigningParameters(false)
    }
  }

  const handleGenerateSkus = async () => {
    if (!id) {
      return
    }

    setIsGeneratingSkus(true)
    try {
      await API.post(`/api/v2/products/${id}/skus/generate`, { all: true })
      toast.success(t('products.detail.variants.generate.success'))
      await mutateSkus()
    } catch (error: unknown) {
      console.error('Generate SKUs error:', error)
      toast.error(t('products.detail.variants.generate.error'), {
        description: extractErrorMessage(error) || t('products.error.description'),
      })
    } finally {
      setIsGeneratingSkus(false)
    }
  }

  const showHistory = (skuId: string) => {
    setSelectedTab(showAllSkusTab && skuId === systemSku?.id ? ALL_SKUS_TAB : skuId)
    historyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  if (isLoading || isSkusLoading) {
    return <Loading />
  }

  if (!product) {
    return (
      <div className="mx-auto w-full max-w-[1280px]">
        <EmptyState icon={Package}
          title={t('products.notFound')}
          ctaButton={
            <Button variant="outline" asChild>
              <Link to="/catalog/products">Zpět na produkty</Link>
            </Button>
          }
        />
      </div>
    )
  }

  const isActive = product.isActive !== false
  const parameters = [...(product.parameters ?? [])].sort((a, b) => a.sortOrder - b.sortOrder)
  const parameterValue = (parameterId: string) => {
    const assigned = product.descriptiveValues?.find((v) => v.parameterId === parameterId)
    if (!assigned) return null
    if (assigned.rawValue) return assigned.rawValue
    const parameter = parameters.find((p) => p.parameterId === parameterId)?.parameter
    return parameter?.values.find((v) => v.id === assigned.parameterValueId)?.label ?? null
  }

  return (
    <>
      <title>{product.name}</title>
      <div className="mx-auto w-full max-w-[1280px]">
        <PageHeader
          title={
            <span className="flex flex-wrap items-center gap-3">
              {product.name}
              {isActive ? (
                <StatusBadge status="paid" label="Aktivní" />
              ) : (
                <StatusBadge status="draft" label="Neaktivní" />
              )}
            </span>
          }
          description={`${productTypeLabel[product.productType ?? 'physical'] ?? product.productType}, ${
            userSkus.length ? formatCount(userSkus.length, 'varianta', 'varianty', 'variant') : 'bez variant'
          }`}
          actions={
            <>
              <Button variant="outline" asChild>
                <Link to={`/catalog/product/${id}/edit`}>
                  <Pencil />
                  {t('products.actions.edit')}
                </Link>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" aria-label="Další akce">
                    <Ellipsis />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to={`/catalog/product/new?copyFrom=${id}`}>
                      <Copy />
                      {t('products.actions.copy')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem variant="destructive" onClick={() => setIsDeleteDialogOpen(true)}>
                    <Trash />
                    Smazat produkt
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          }
        />

        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="grid min-w-0 gap-6">
            <Panel title="Základní údaje">
              <dl className="grid gap-x-6 gap-y-4 text-sm sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <dt className="text-xs text-muted-foreground">{t('products.detail.description')}</dt>
                  <dd className="mt-1 whitespace-pre-line text-pretty">
                    {product.description || <span className="text-muted-foreground">Bez popisu</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">{t('products.detail.productType')}</dt>
                  <dd className="mt-1">{productTypeLabel[product.productType ?? 'physical'] ?? product.productType}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">{t('products.detail.externalId')}</dt>
                  <dd className="mt-1 tabular">{product.externalId || <span className="text-muted-foreground">-</span>}</dd>
                </div>
                {product.createdAt && (
                  <div>
                    <dt className="text-xs text-muted-foreground">Vytvořeno</dt>
                    <dd className="mt-1 tabular">{formatDate(product.createdAt)}</dd>
                  </div>
                )}
              </dl>
            </Panel>

            <Panel
              title={t('products.detail.variants.title')}
              action={
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setIsAssignAxesOpen(true)}>
                    {t('products.detail.variants.assign.action')}
                  </Button>
                  <Button size="sm" onClick={handleGenerateSkus} disabled={isGeneratingSkus || assignedAxes.length === 0}>
                    {t('products.detail.variants.generate.action')}
                  </Button>
                </div>
              }
            >
              {assignedAxes.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('products.detail.variants.empty')}</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {assignedAxes.map((assignment) => (
                    <span key={assignment.axisId} className="inline-flex h-6 items-center rounded-full bg-muted px-2.5 text-xs">
                      {assignment.axis?.name ?? assignment.axisId}
                    </span>
                  ))}
                </div>
              )}
            </Panel>

            <ProductVariants
              product={product}
              skus={variantSkus}
              systemSku={systemSku}
              stockBySkuId={stockBySkuId}
              onChanged={refreshCatalog}
              onAdd={() => setIsCreateSkuDialogOpen(true)}
              onEdit={setSkuToEdit}
              onDelete={setSkuToDelete}
              onShowHistory={showHistory}
            />

            {warehouseEnabled && (
              <section>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h2 className="text-sm font-medium">{t('products.detail.stock.title')}</h2>
                  <span className="text-xs text-muted-foreground">Zásoby variant po skladech</span>
                </div>
                <StockItems
                  stockItems={productStockItems}
                  isLoading={isStockLoading}
                  view="byProduct"
                  emptyMessage="Produkt zatím nemá v žádném skladu zásobu."
                  skuLabel={(item) => {
                    const sku = skuById.get(item.skuId)
                    return (
                      <p className="truncate font-medium">
                        {sku ? variantLabel(sku, product.name) : item.skuName || item.skuCode}
                      </p>
                    )
                  }}
                  onAdjusted={() => mutateStockItems()}
                  onTransfer={warehouses.length > 1 ? setStockItemToTransfer : undefined}
                  onEditBinLocation={setStockItemToEditLocation}
                />
              </section>
            )}

            <Panel
              title={t('products.detail.parameters.title')}
              action={
                <Button variant="outline" size="sm" onClick={() => setIsAssignParametersOpen(true)}>
                  {t('products.detail.parameters.assign.action')}
                </Button>
              }
            >
              {parameters.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('products.detail.parameters.empty')}</p>
              ) : (
                <dl className="divide-y border-y">
                  {parameters.map((p) => {
                    const value = parameterValue(p.parameterId)
                    return (
                      <div key={p.parameterId} className="flex items-baseline justify-between gap-4 py-2.5 text-sm">
                        <dt className="text-muted-foreground">{p.parameter.name}</dt>
                        <dd className="text-right">{value ?? <span className="text-muted-foreground">Nevyplněno</span>}</dd>
                      </div>
                    )
                  })}
                </dl>
              )}
            </Panel>

            {gallerySku && (
              <Panel title={gallerySku.isSystem ? t('products.detail.gallery.title') : `${t('products.detail.gallery.title')}: ${variantLabel(gallerySku, product.name)}`}>
                {gallerySku.isSystem && (
                  <p className="mb-4 text-sm text-muted-foreground">{t('products.detail.gallery.productDescription')}</p>
                )}
                <SkuGallery sku={gallerySku} onChange={() => mutateSkus()} />
              </Panel>
            )}

            <section ref={historyRef} className="scroll-mt-6 rounded-lg border p-5">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-sm font-medium">{t('products.detail.priceHistory.title')}</h2>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {isAllSkusTab
                      ? t('products.detail.priceHistory.allDescription')
                      : 'Naplánujte novou cenu od zvoleného data nebo si projděte dřívější ceny.'}
                  </p>
                </div>
              </div>

              {showAllSkusTab && (
                <SegmentedTabs<string>
                  className="mb-4"
                  value={activeTab ?? ALL_SKUS_TAB}
                  onChange={setSelectedTab}
                  options={[
                    { value: ALL_SKUS_TAB, label: 'Všechny varianty' },
                    ...userSkus.map((sku) => ({ value: sku.id, label: variantLabel(sku, product.name) })),
                  ]}
                />
              )}

              {!activeSkuId ? (
                <p className="py-6 text-center text-sm text-muted-foreground">{t('products.detail.skus.empty')}</p>
              ) : isPriceHistoryLoading ? (
                <Loading />
              ) : (
                <div className="space-y-4">
                  <ProductPriceForm
                    variant="inline"
                    skuId={activeSkuId}
                    successMessage={isAllSkusTab ? t('products.price.add.allSuccess') : undefined}
                    onSuccess={refreshCatalog}
                  />
                  {priceHistory.length > 0 && <PriceTimelineChart prices={priceHistory} />}
                  {priceHistory.length > 0 ? (
                    <div className="overflow-hidden rounded-lg border">
                      <Table>
                        <TableHeader>
                          <TableRow className="hover:bg-transparent">
                            <TableHead>{t('products.detail.priceHistory.table.validFrom')}</TableHead>
                            <TableHead className="hidden md:table-cell">{t('products.detail.priceHistory.table.validTo')}</TableHead>
                            <TableHead className="text-right">{t('products.detail.priceHistory.table.price')}</TableHead>
                            <TableHead className="hidden text-right md:table-cell">{t('products.detail.priceHistory.table.taxRate')}</TableHead>
                            <TableHead className="hidden md:table-cell">{t('products.detail.priceHistory.table.note')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {priceHistory.map((price) => (
                            <TableRow key={`${price.validFrom}-${price.validTo ?? 'current'}`} className="hover:bg-transparent">
                              <TableCell className="w-full max-w-0 md:w-auto md:max-w-none">
                                {formatDate(price.validFrom)}
                                <p className="truncate text-xs text-muted-foreground md:hidden">
                                  {[
                                    price.validTo
                                      ? `do ${formatDate(price.validTo)}`
                                      : t('products.detail.priceHistory.table.current'),
                                    price.taxRate ? `DPH ${price.taxRate.percentage} %` : null,
                                    price.note,
                                  ]
                                    .filter(Boolean)
                                    .join(', ')}
                                </p>
                              </TableCell>
                              <TableCell className="hidden md:table-cell">
                                {price.validTo ? (
                                  formatDate(price.validTo)
                                ) : (
                                  <StatusBadge status="paid" label={t('products.detail.priceHistory.table.current')} />
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <Money amount={price.price.amount} currency={price.price.currency} />
                              </TableCell>
                              <TableCell className="hidden text-right md:table-cell">
                                {price.taxRate ? `${price.taxRate.percentage} %` : '-'}
                              </TableCell>
                              <TableCell className="hidden max-w-64 truncate text-muted-foreground md:table-cell">{price.note || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">{t('products.detail.priceHistory.empty')}</p>
                  )}
                </div>
              )}
            </section>
          </div>

          <aside className="grid gap-6">
            <Panel title={t('products.detail.status')}>
              <label className="flex items-center justify-between gap-3 text-sm">
                <span>{isActive ? 'Aktivní' : 'Neaktivní'}</span>
                <Switch checked={isActive} disabled={savingActive} onCheckedChange={toggleActive} />
              </label>
            </Panel>

            <Panel
              title="Kategorie"
              action={
                <Button variant="link" size="sm" asChild>
                  <Link to={`/catalog/product/${id}/edit`}>Změnit</Link>
                </Button>
              }
            >
              <div className="space-y-4 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">{t('products.detail.mainCategory')}</p>
                  {product.mainCategoryId ? (
                    <Link
                      to={`/catalog/products?mainCategoryId=${product.mainCategoryId}`}
                      className="mt-1 block hover:underline"
                    >
                      {mainCategoryPath ?? '-'}
                    </Link>
                  ) : (
                    <p className="mt-1 text-muted-foreground">-</p>
                  )}
                </div>
                {secondaryCategories.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground">{t('products.detail.secondaryCategories')}</p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {secondaryCategories.map((cat) => (
                        <span key={cat.id} className="inline-flex h-6 items-center rounded-full bg-muted px-2.5 text-xs">
                          {categoryPath(categories, cat.id)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Panel>
          </aside>
        </div>
      </div>

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        setIsOpen={setIsDeleteDialogOpen}
        title={t('products.delete.confirm.title', { name: product.name })}
        description={t('products.delete.confirm.description')}
        onConfirm={handleDeleteProduct}
      />

      <ConfirmDialog
        isOpen={!!skuToDelete}
        setIsOpen={(open) => {
          if (!open) setSkuToDelete(null)
        }}
        title={t('products.detail.skus.delete.confirm.title', { name: skuToDelete?.name ?? '' })}
        description={t('products.detail.skus.delete.confirm.description')}
        onConfirm={handleDeleteSku}
      />

      <Dialog
        open={!!skuToEdit}
        onOpenChange={(open) => {
          if (!open && isEditSkuSubmitting) return
          if (!open) setSkuToEdit(null)
        }}
      >
        <DialogContent>
          <DialogTitle>{t('products.detail.skus.edit.title')}</DialogTitle>
          <DialogDescription>{t('products.detail.skus.edit.description')}</DialogDescription>
          {skuToEdit && (
            <SkuEditForm
              key={skuToEdit.id}
              sku={skuToEdit}
              onSubmittingChange={setIsEditSkuSubmitting}
              onSuccess={async () => {
                setSkuToEdit(null)
                await mutateSkus()
              }}
              onCancel={() => setSkuToEdit(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={isCreateSkuDialogOpen}
        onOpenChange={(open) => {
          if (!open && isCreateSkuSubmitting) return
          setIsCreateSkuDialogOpen(open)
        }}
      >
        <DialogContent>
          <DialogTitle>{t('products.detail.skus.create.title')}</DialogTitle>
          <DialogDescription>{t('products.detail.skus.create.description')}</DialogDescription>
          {isCreateSkuDialogOpen && id && (
            <SkuCreateForm
              productId={id}
              productSlug={product.slug}
              onSubmittingChange={setIsCreateSkuSubmitting}
              onSuccess={async (sku) => {
                setSelectedTab(sku.id)
                setIsCreateSkuDialogOpen(false)
                await mutateSkus()
              }}
              onCancel={() => setIsCreateSkuDialogOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {warehouseEnabled && (
        <>
          <StockTransferForm
            isOpen={!!stockItemToTransfer}
            onClose={() => setStockItemToTransfer(undefined)}
            warehouses={warehouses}
            defaultFromWarehouseId={stockItemToTransfer?.warehouseId}
            defaultSkuId={stockItemToTransfer?.skuId}
            defaultBatchNumber={stockItemToTransfer?.batchNumber}
            onSuccess={() => mutateStockItems()}
          />
          <StockBinLocationForm
            isOpen={!!stockItemToEditLocation}
            onClose={() => setStockItemToEditLocation(undefined)}
            stockItem={stockItemToEditLocation}
            onSuccess={() => mutateStockItems()}
          />
        </>
      )}

      <Dialog open={isAssignAxesOpen} onOpenChange={setIsAssignAxesOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('products.detail.variants.assign.title')}</DialogTitle>
            <DialogDescription>{t('products.detail.variants.assign.description')}</DialogDescription>
          </DialogHeader>
          <div className="max-h-80 space-y-3 overflow-y-auto">
            <VariantAxisChecklist value={selectedAxisIds} onChange={setSelectedAxisIds} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsAssignAxesOpen(false)} disabled={isAssigningAxes}>
              {t('products.detail.variants.assign.cancel')}
            </Button>
            <Button type="button" onClick={handleAssignAxes} disabled={isAssigningAxes}>
              {t('products.detail.variants.assign.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isAssignParametersOpen} onOpenChange={setIsAssignParametersOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('products.detail.parameters.assign.title')}</DialogTitle>
            <DialogDescription>{t('products.detail.parameters.assign.description')}</DialogDescription>
          </DialogHeader>
          <ParameterChecklist value={selectedParameters} onChange={setSelectedParameters} />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsAssignParametersOpen(false)}
              disabled={isAssigningParameters}
            >
              {t('products.detail.parameters.assign.cancel')}
            </Button>
            <Button type="button" onClick={handleAssignParameters} disabled={isAssigningParameters}>
              {t('products.detail.parameters.assign.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
